import axios from 'axios';

/**
 * The shared axios instance for all API calls. Base URL comes from the runtime
 * config (window.config.apiUrl) — never a build-time env var — matching the
 * OWS-Reporting-Tool convention.
 */
const http = axios.create({
  baseURL: window.config.apiUrl,
});

/**
 * The acting member id, sent on every request as `X-Actor-Id` so the backend
 * attributes writes (created_by/updated_by, activity authorship) and scopes
 * presence to the right person. Set by the identity layer at sign-in; seeded from
 * localStorage at module load so a page reload stays signed in before React mounts.
 */
const ACTOR_KEY = 'spark-board.actorId';
let actorId: string | null = typeof localStorage !== 'undefined' ? localStorage.getItem(ACTOR_KEY) : null;

export const setActorId = (id: string | null) => {
  actorId = id;
  try {
    if (id) localStorage.setItem(ACTOR_KEY, id);
    else localStorage.removeItem(ACTOR_KEY);
  } catch {
    /* ignore storage failures (private mode) */
  }
};

export const getActorId = (): string | null => actorId;

/**
 * The Zitadel ID token, sent as `Authorization: Bearer` when OIDC is enabled so the
 * backend can verify the caller and derive the acting member from the token (rather than
 * trusting X-Actor-Id). Kept in memory only; the oidc-client-ts library owns durable token
 * storage and hands us a fresh token via the identity layer on login and on silent renewal.
 */
let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const getAccessToken = (): string | null => accessToken;

/**
 * Called on a 401 (expired/invalid token) so the identity layer can re-authenticate. The
 * OIDC identity provider registers a handler that redirects to Zitadel; legacy mode leaves
 * it unset. A one-shot guard prevents a redirect storm when many requests 401 at once.
 */
let onUnauthorized: (() => void) | null = null;
export const setOnUnauthorized = (fn: (() => void) | null) => {
  onUnauthorized = fn;
};
let handlingUnauthorized = false;

/**
 * A stable id for THIS browser tab (not persisted). Sent as `X-Client-Id` and echoed
 * back on real-time events, so a tab can skip its own echo while a SECOND tab of the
 * same member still applies the update (member-id suppression would hide it there).
 */
export const CLIENT_ID: string = (() => {
  try {
    return crypto.randomUUID();
  } catch {
    return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
})();

http.interceptors.request.use((config) => {
  // OIDC: the verified token is authoritative for identity. Legacy: X-Actor-Id names the member.
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);
  if (actorId) config.headers.set('X-Actor-Id', actorId);
  config.headers.set('X-Client-Id', CLIENT_ID);
  return config;
});

// Re-authenticate on a 401 (expired/invalid token). A 403 (forbidden by role) is NOT an
// auth failure and must pass through so the UI can show "not allowed" instead of looping.
http.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && onUnauthorized && !handlingUnauthorized) {
      handlingUnauthorized = true;
      try {
        onUnauthorized();
      } finally {
        // Release after the current tick so a burst of parallel 401s triggers one redirect.
        window.setTimeout(() => {
          handlingUnauthorized = false;
        }, 2000);
      }
    }
    return Promise.reject(error);
  },
);

export default http;
